import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { auth, db } from '../firebaseConfig';
import { useAuthState } from 'react-firebase-hooks/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { canAccessApp, isTrialExpired, calculateTrialStatus as calculateTrialStatusUtil } from '../utils/trialUtils';

const OrganizationContext = createContext();

// Max number of retries when fetching organization data
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

export function OrganizationProvider({ children }) {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [state, setState] = useState({
    organizationId: null,
    organizationData: null,
    loading: false,
    error: null,
    isApproved: false,
    userApproved: false, // Separate field for user approval status
    userRole: null,
    lastUpdate: null,
    // Trial-related state (safely added)
    trialStatus: null,
    canAccessApp: false,
    isTrialExpired: false
  });
  const stateRef = useRef(state);

  // Log when user state changes
  useEffect(() => {
    // User state change handling
  }, [user]);

  const updateState = useCallback((updates) => {
    setState(prev => {
      const newState = { ...prev, ...updates, lastUpdate: new Date().toISOString() };
      return newState;
    });
  }, []);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const normalizeDateValue = useCallback((value) => {
    if (!value) return null;

    if (typeof value.toDate === 'function') {
      const date = value.toDate();
      return isNaN(date.getTime()) ? null : date.toISOString();
    }

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value.toISOString();
    }

    if (typeof value === 'number') {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date.toISOString();
    }

    if (typeof value === 'string') {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date.toISOString();
    }

    return null;
  }, []);

  // Helper function to calculate trial status from organization data
  const calculateTrialStatus = useCallback((orgData) => {
    if (!orgData) return null;

    // Initialize trial data if not present (for existing organizations)
    const trialStartDate =
      normalizeDateValue(orgData.trialStartDate) ||
      normalizeDateValue(orgData.createdAt);

    let trialEndDate = normalizeDateValue(orgData.trialEndDate);

    if (!trialEndDate && trialStartDate) {
        const start = new Date(trialStartDate);
      if (!isNaN(start.getTime())) {
        const end = new Date(start);
        end.setDate(end.getDate() + 30);
        trialEndDate = end.toISOString();
      }
    }

    const subscriptionStatus = orgData.subscriptionStatus || 'trial';
    
    // Use the trialUtils function for consistent calculation
    const trialStatus = calculateTrialStatusUtil(trialStartDate, trialEndDate);

    return {
      subscriptionStatus,
      trialStatus,
      trialStartDate,
      trialEndDate,
      subscriptionPlan: orgData.subscriptionPlan || null
    };
  }, [normalizeDateValue]);

  // Function to fetch data with retries
  const fetchWithRetry = useCallback(async (fetchFn, retries = MAX_RETRIES) => {
    try {
      return await fetchFn();
    } catch (err) {
      if (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return fetchWithRetry(fetchFn, retries - 1);
      }
      throw err;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    let unsubscribeOrg = null;
    let unsubscribeUser = null;
    let userDataFetched = false; // Track if user data has been fetched to avoid repeated calls

    async function fetchOrganizationData() {
      if (!user) {
        if (mounted) {
          updateState({
            organizationId: null,
            organizationData: null,
            error: null,
            isApproved: false,
            userRole: null,
            loading: false,
            // Clear trial-related state
            trialStatus: null,
            canAccessApp: false,
            isTrialExpired: false
          });
        }
        return;
      }

      if (mounted) {
        updateState({ loading: true, error: null });
      }

      try {
        // 1. Get the user document with organization ID using retry logic
        // This replaces the fixed delay with proper retry mechanism
        const userRef = doc(db, 'users', user.uid);
        
        const getUserDocWithRetry = async (retries = MAX_RETRIES) => {
          try {
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
              return userDoc;
            }
            // Document doesn't exist yet, retry if we have retries left
            if (retries > 0) {
              await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
              return getUserDocWithRetry(retries - 1);
            }
            return userDoc; // Return even if it doesn't exist (after all retries)
          } catch (err) {
            if (retries > 0) {
              await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
              return getUserDocWithRetry(retries - 1);
            }
            throw err;
          }
        };
        
        const userDoc = await getUserDocWithRetry();
        
        if (!userDoc.exists()) {
          if (mounted) {
            updateState({
              error: 'No organization associated with user. Please register with an organization.',
              loading: false
            });
          }
          return;
        }

        const orgId = userDoc.data().organizationId;

        if (!orgId) {
          if (mounted) {
            updateState({
              error: 'No organization associated with user. Please register with an organization.',
              loading: false
            });
          }
          return;
        }

        // 2. Set up real-time listener for organization document
        const orgRef = doc(db, 'organizations', orgId);
        const orgUserRef = doc(db, `organizations/${orgId}/users/${user.uid}`);
        
        // Set up organization listener
        unsubscribeOrg = onSnapshot(orgRef, async (orgDoc) => {

          if (!mounted) return;

          if (!orgDoc.exists()) {
            updateState({
              error: 'Organization not found',
              loading: false
            });
            return;
          }

          const orgData = orgDoc.data();
          
          // Only fetch user data if we haven't already or if organization data changed
          if (!userDataFetched || !stateRef.current.organizationData || 
              JSON.stringify(orgData) !== JSON.stringify(stateRef.current.organizationData)) {
            
            // Fetch user data with a one-time getDoc call instead of listener
            try {
              const orgUserDoc = await getDoc(orgUserRef);
              
              if (!mounted) return;

              if (!orgUserDoc.exists()) {
                // Immediately sign out and clear context to prevent bypass
                if (mounted) {
                  updateState({
                    organizationId: null,
                    organizationData: null,
                    error: 'User not found in organization',
                    isApproved: false,
                    userRole: null,
                    loading: false,
                    // Clear trial-related state
                    trialStatus: null,
                    canAccessApp: false,
                    isTrialExpired: false
                  });
                  auth.signOut();
                  navigate('/login');
                }
                return;
              }

              const userData = orgUserDoc.data();

              const calculatedIsApproved = orgData.approved === true && userData.approved === true;

              // Calculate trial status
              const trialData = calculateTrialStatus(orgData);
              // FIX: Issue #3 - Pass subscriptionEndsAt to canAccessApp for expiration check
              const subscriptionEndsAt = orgData.subscriptionEndsAt || null;
              const canAccess = canAccessApp(calculatedIsApproved, trialData?.subscriptionStatus, trialData?.trialStatus, orgData?.bypassLimits, subscriptionEndsAt);
              const trialExpired = isTrialExpired(trialData?.subscriptionStatus, trialData?.trialStatus, subscriptionEndsAt);

              // Check if user access has been revoked
              if (!userData.approved && stateRef.current.isApproved) {
                navigate('/pending-approval', { state: { message: 'Your access has been revoked.' } });
              }

              updateState({
                organizationId: orgId,
                organizationData: orgData,
                isApproved: calculatedIsApproved,
                userApproved: userData.approved, // Store user approval status separately
                userRole: userData.role,
                error: null,
                loading: false,
                // Trial-related state
                trialStatus: trialData,
                canAccessApp: canAccess,
                isTrialExpired: trialExpired
              });

              userDataFetched = true;
            } catch (error) {
              // console.error('Error fetching user data:', error);
              if (mounted) {
                updateState({
                  error: `Error fetching user data: ${error.message}`,
                  loading: false
                });
              }
            }
          } else {
            // Organization data structure hasn't changed, but subscription status might have
            // We still need to recalculate trial status, canAccessApp, and isTrialExpired
            // This is critical because subscription status might have changed (trial expired, subscription activated, etc.)
            const trialData = calculateTrialStatus(orgData);
            const subscriptionEndsAt = orgData.subscriptionEndsAt || null;
            
            // Recalculate canAccessApp and isTrialExpired even if org data structure unchanged
            // Use existing approval status from state (already calculated from user data)
            const calculatedIsApproved = stateRef.current.isApproved;
            const canAccess = canAccessApp(
              calculatedIsApproved,
              trialData?.subscriptionStatus,
              trialData?.trialStatus,
              orgData?.bypassLimits,
              subscriptionEndsAt
            );
            const trialExpired = isTrialExpired(trialData?.subscriptionStatus, trialData?.trialStatus, subscriptionEndsAt);
            
            updateState({
              organizationId: orgId,
              organizationData: orgData,
              error: null,
              loading: false,
              // Always update trial-related state
              trialStatus: trialData,
              canAccessApp: canAccess,
              isTrialExpired: trialExpired
            });
          }
        }, (error) => {
          // console.error('Error in organization listener:', error);
          if (mounted) {
            updateState({
              error: `Error fetching organization: ${error.message}`,
              loading: false
            });
          }
        });

        // CRITICAL SECURITY: Set up real-time listener for user's organization membership
        // This provides immediate detection of access revocation or user deletion
        unsubscribeUser = onSnapshot(orgUserRef, (userDoc) => {

          if (!mounted) return;

          if (!userDoc.exists()) {
            // Immediately sign out and clear context to prevent any data access
            if (mounted) {
              updateState({
                organizationId: null,
                organizationData: null,
                error: 'User access has been revoked or account deleted',
                isApproved: false,
                userRole: null,
                loading: false,
                // Clear trial-related state
                trialStatus: null,
                canAccessApp: false,
                isTrialExpired: false
              });
              auth.signOut();
              navigate('/login');
            }
            return;
          }

          const userData = userDoc.data();
          
          // Check if user approval status has changed
          if (!userData.approved && stateRef.current.isApproved) {
            // Immediately redirect to pending approval
            if (mounted) {
              updateState({
                isApproved: false,
                userRole: userData.role
              });
              navigate('/pending-approval', { 
                state: { message: 'Your access has been revoked.' } 
              });
            }
            return;
          }

          // Check if user role has changed
          if (userData.role !== stateRef.current.userRole) {
            if (mounted) {
              updateState({
                userRole: userData.role
              });
            }
          }

          // Update approval status if it changed
          if (userData.approved !== stateRef.current.isApproved) {
            if (mounted) {
              updateState({
                isApproved: userData.approved
              });
            }
          }
        }, (error) => {
          // console.error('Error in user organization membership listener:', error);
          if (mounted) {
            updateState({
              error: `Error monitoring user access: ${error.message}`,
              loading: false
            });
          }
        });

      } catch (error) {
        // console.error('Error in fetchOrganizationData:', error);
        if (mounted) {
          updateState({
            error: `Error: ${error.message}`,
            loading: false
          });
        }
      }
    }

    fetchOrganizationData();

    return () => {
      mounted = false;
      if (unsubscribeOrg) {
        unsubscribeOrg();
      }
      if (unsubscribeUser) {
        unsubscribeUser();
      }
    };
  }, [user, calculateTrialStatus, navigate, updateState]);

  // SECURITY IMPROVEMENT: Added real-time user organization membership listener alongside
  // the organization listener. This provides immediate detection of access revocation,
  // user deletion, role changes, and approval status changes in real-time.
  // The user listener is essential for security as it monitors the specific document
  // that controls user access to organization data.

  const contextValue = useMemo(() => ({
    ...state,
    refetch: () => {
      updateState({ loading: true, error: null });
    }
  }), [state, updateState]);

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
} 